import { FC, useContext } from "react";
import {
  AspectRatio,
  Box,
  Flex,
  Paper,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useNetwork } from "@mantine/hooks";
import { useCompetition } from "../../../../hooks/useCompetition.ts";
import { CompetitionContext } from "../../../../CompetitionContext.tsx";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin.ts";
import { useIsDesktop } from "../../../../hooks/useIsDesktop.ts";
import { IconPlayerPlay, IconCalendarEvent } from "@tabler/icons-react";
import { DateTime } from "luxon";

export const Stream: FC = () => {
  const network = useNetwork();
  const autostart = network.type != "cellular" && !network.saveData;
  const isDesktop = useIsDesktop();
  const theme = useMantineTheme();

  const competitionId = useContext(CompetitionContext);
  const competition = useCompetition(competitionId);

  if (!competition.isSuccess) {
    return null;
  }

  // is competition currently running (start and end date are only dates, no time)?
  const now = new Date();
  const startDate = new Date(competition.data.start);
  const endDate = new Date(competition.data.end);
  endDate.setHours(23, 59, 59, 999); // set end date to end of the day

  const isBefore = now < startDate;
  const isAfter = now > endDate;

  if (isBefore || isAfter) {
    // show placeholder if competition is not running
    if (!isDesktop) return null;

    const startDateTime = DateTime.fromJSDate(startDate);
    const formattedDate = startDateTime.toFormat("dd.MM.yyyy");

    return (
      <Paper
        radius="md"
        p={0}
        style={{
          overflow: "hidden",
        }}
      >
        <AspectRatio ratio={16 / 9}>
          <Box
            style={{
              width: "100%",
              height: "100%",
              background: `linear-gradient(135deg, ${theme.colors.fwcBlue[9]} 0%, ${theme.colors.fwcBlue[7]} 50%, ${theme.colors.fwcBlue[5]} 100%)`,
              position: "relative",
            }}
          >
            {/* Decorative circles */}
            <Box
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <Box
              style={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              }}
            />

            {/* Content */}
            <Flex
              direction="column"
              align="center"
              justify="center"
              h="100%"
              gap="md"
              style={{ position: "relative", zIndex: 1 }}
            >
              {/* Play icon with ring */}
              <Box
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  border: "3px solid rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.1)",
                }}
              >
                <IconPlayerPlay
                  size={36}
                  color="white"
                  style={{ marginLeft: 4 }}
                />
              </Box>

              <Flex direction="column" align="center" gap={4}>
                <Text c="white" size="lg" fw={600}>
                  Livestream
                </Text>
                <Text c="white" opacity={0.8} size="sm" ta="center" px="xl">
                  {isBefore
                    ? "Der Livestream startet am Turniertag."
                    : "Das Turnier ist beendet. Vielen Dank fürs Zuschauen!"}
                </Text>
              </Flex>

              {isBefore && (
                <Flex
                  align="center"
                  gap="xs"
                  mt="xs"
                  px="md"
                  py="xs"
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: 20,
                  }}
                >
                  <IconCalendarEvent size={16} color="white" />
                  <Text c="white" size="sm" fw={500}>
                    {formattedDate}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Box>
        </AspectRatio>
      </Paper>
    );
  }

  const streamURL = getPresentationParamsForOrigin().streamURL(autostart);

  return (
    <Paper radius="md" p={0} style={{ overflow: "hidden" }}>
      <AspectRatio ratio={16 / 9}>
        <iframe
          title="Livestream"
          style={{
            border: "none",
            width: "100%",
            height: "100%",
            display: "block",
          }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          src={streamURL}
        />
      </AspectRatio>
    </Paper>
  );
};
